<script setup>
import { useAppStore } from '~/store/main'

const props = defineProps({
  img: { type: Object, default: () => ({}) },
  mobilImg: { type: Object, default: () => {} },
  link: { type: Object, default: () => ({}) },
  video: { type: String, default: '' },
  title: { type: String, default: '' },
  from: { type: String, default: '' },
  fromHomePage: { type: Boolean, default: false },
  externalLink: { type: String, default: '' },
})

const videoIsReady = ref(false)
const player = ref(null)
const youtubePlayerContainer = ref(null)
const thumbnailUrl = ref('')

const localePath = useLocalePath()
const store = useAppStore()
const getHeaderHeight = computed(() => store.getHeaderHeight)
const getIsDesktop = computed(() => store.getIsDesktop)

const videoId = computed(() => {
  if (props.video) {
    const regExp =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/
    const match = props.video.match(regExp)
    return match && match[7].length == 11 ? match[7] : false
  }
  return ''
})

const iframeSrc = computed(() => {
  if (!videoId.value) return ''

  const currentOrigin =
    typeof window !== 'undefined' ? window.location.origin : 'https://avu.cz'

  const params = new URLSearchParams({
    autoplay: 1,
    mute: 1,
    playsinline: 1,
    controls: 0,
    loop: 1,
    playlist: videoId.value,
    modestbranding: 1,
    rel: 0,
    enablejsapi: 1,
    origin: currentOrigin,
  })

  return `https://www.youtube-nocookie.com/embed/${videoId.value}?${params.toString()}`
})

const aspect = computed(() => {
  if (
    getIsDesktop.value &&
    props.from != 'employee' &&
    props.from != 'publication'
  ) {
    return '16 / 9'
  } else {
    return '4 / 6'
  }
})

async function getThumbnail() {
  const qualities = ['maxresdefault.jpg', 'sddefault.jpg', 'hqdefault.jpg']
  for (const quality of qualities) {
    const url = `https://img.youtube.com/vi/${videoId.value}/${quality}`
    try {
      const response = await fetch(url, { method: 'HEAD' })
      if (response.ok) {
        const image = new Image()
        image.src = url
        await new Promise((resolve) => {
          image.onload = resolve
        })
        if (image.width > 120) {
          thumbnailUrl.value = url
          return
        }
      }
    } catch (error) {
      console.error(`Error fetching thumbnail ${url}:`, error)
    }
  }
}

async function loadYouTubeAPI() {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT)
    } else {
      const tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      const firstScriptTag = document.getElementsByTagName('script')[0]
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)

      window.onYouTubeIframeAPIReady = () => {
        resolve(window.YT)
      }
    }
  })
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') {
    if (player.value && videoIsReady.value) {
      player.value.playVideo()
    }
  }
}

onMounted(async () => {
  if (!videoId.value) return

  getThumbnail()

  const YT = await loadYouTubeAPI()

  await nextTick()

  if (youtubePlayerContainer.value) {
    player.value = new YT.Player(youtubePlayerContainer.value, {
      events: {
        onReady: (event) => {
          event.target.mute()
          setTimeout(() => {
            event.target.playVideo()
          }, 300)
        },
        onStateChange: (event) => {
          if (event.data === YT.PlayerState.PLAYING) {
            videoIsReady.value = true
          }
          if ((event.data === 5 || event.data === -1) && !videoIsReady.value) {
            event.target.playVideo()
          }
        },
      },
    })
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  if (videoId.value) {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  }
})
</script>

<template>
  <section class="hero">
    <ClientOnly>
      <div
        v-if="img.node && !video"
        class="aspect_img"
        :style="{ aspectRatio: aspect }"
      >
        <NuxtImg
          :src="
            getIsDesktop
              ? img.node.sourceUrl
              : mobilImg?.mediaItemUrl || img.node.sourceUrl
          "
          :alt="img.node.altText || img.node.caption"
        />
      </div>
    </ClientOnly>

    <div v-if="link?.heroExternalLink" class="video__link">
      <NuxtLink :href="link.heroExternalLink" target="_blank" class="button">
        <span class="herolink">{{ link?.heroTextOdkazu }}</span>
      </NuxtLink>
    </div>

    <div v-if="link && link.heroLink && link.heroLink.slug" class="video__link">
      <NuxtLink
        :to="
          localePath({
            name: 'article-slug',
            params: { slug: link.heroLink.slug },
          })
        "
        class="button"
      >
        <span class="herolink">{{ link.heroTextOdkazu }}</span>
      </NuxtLink>
    </div>

    <div
      v-if="video"
      class="video"
      :style="{
        height: fromHomePage ? `calc(100vh - ${getHeaderHeight + 64}px)` : '',
        'padding-bottom': fromHomePage ? 0 : '',
      }"
    >
      <div
        class="aspect_video"
        :style="{
          paddingBottom: fromHomePage
            ? `max(calc(100vh - ${getHeaderHeight + 64}px), 60%)`
            : '',
        }"
      >
        <div
          class="aspect_video__wall"
          :style="{
            'background-image': `url('${thumbnailUrl}')`,
          }"
          :class="{ 'aspect_video__wall--ready': videoIsReady }"
        ></div>

        <ClientOnly>
          <iframe
            v-if="iframeSrc"
            id="youtuber"
            ref="youtubePlayerContainer"
            :src="iframeSrc"
            frameborder="0"
            :title="title || 'Hero video'"
            allow="autoplay; encrypted-media; picture-in-picture"
            referrerpolicy="strict-origin-when-cross-origin"
            style="
              width: 100%;
              height: 100%;
              position: absolute;
              top: 0;
              left: 0;
            "
          />
        </ClientOnly>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
/* Стилі залишаємо без змін */
.hero {
  position: relative;
  margin: 0 -20px;
  @include media-breakpoint-up(sm) {
    margin: 0 -40px;
  }
  @media screen and (min-width: 1800px) {
    width: calc(100vw - 20px);
    margin: 0px;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
  }
}
</style>
